import { Dialog, Popover } from '@mui/material'
import { useRef, useState } from 'react'
import { PRESCRIPTION_BUILDER_VIEW_TYPES } from 'src/consumer/constants'
import { getFirstLetterCapitalizedString } from 'src/consumer/helpers/utilFunctions'
import {
  commonStylesPrescriptionSelect,
  loadOptionsObservations,
} from '../../PrescriptionMakingPageComponents'
import { ObservationsCreatableSelect } from '../../PrescriptionMakingPageStyles'
import { PRESCRIPTION_COLORS_MAP } from '../common/color-utils'
import HealthEntityAddPopover from '../common/HealthEntityAddPopover'
import PrescriptionPillViewInputComponent from '../common/PrescriptionPillViewInputComponent'
import PrescriptionTableViewInputComponent from '../common/PrescriptionTableViewInputComponent'
import ObservationsContextForm from './ObservationsContextForm'
import { handleChangeHealthEntityInputType1, handleKeyDownAndSupportEdit } from '../common/helpers'

export default function ObservationsInputComponent({
  clinicId,
  clinicBrandId,
  observations,
  setSelectedObservations,
  recommendedObservations = [],
  prescriptionViewSettings,
  contextMenuSide,
}) {
  const [addObservationButtonAnchor, setAddObservationButtonAnchor] = useState(false)
  const [observationToCreate, setObservationToCreate] = useState(null)

  async function createObservations(diagnosisToAdd) {
    setSelectedObservations((prev) => [...(prev || []), diagnosisToAdd])
    //fire api to create instruction
  }

  function onCreateObservations(inputValue, e) {
    const observationToAdd = {
      apiParam: 'health-observation',
      value: inputValue,
      label: inputValue,
      __isNew__: true,
    }
    setObservationToCreate(observationToAdd)
    if (!!e) {
      setAddObservationButtonAnchor(e.currentTarget)
    } else {
      setAddObservationButtonAnchor(true)
    }
  }

  function onChangeForNewUIItems(allValuesWithSelectedValues) {
    setSelectedObservations(
      allValuesWithSelectedValues?.map((item) => {
        const label = item?.label || item?.value
        return { ...item, label: getFirstLetterCapitalizedString(label) }
      }),
    )
  }

  switch (prescriptionViewSettings?.prescriptionBuilderViewType) {
    case PRESCRIPTION_BUILDER_VIEW_TYPES.tabletView:
      return (
        <>
          <PrescriptionPillViewInputComponent
            additional={{
              page: 1,
              clinicId: clinicId,
              clinicBrandId: clinicBrandId,
              recommendedObservations,
              prescriptionViewSettings,
            }}
            searchPlaceholder="Observation"
            onChange={onChangeForNewUIItems}
            onCreate={onCreateObservations}
            prescriptionViewSettings={prescriptionViewSettings}
            debounceTimeout={200}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            getOptionsResponse={loadOptionsObservations}
            value={observations}
            itemName={'observation'}
            ContextMenuFormComponent={ObservationsContextForm}
            contextMenuOpenSide={contextMenuSide}
            pillColor={PRESCRIPTION_COLORS_MAP.observations.color}
            highlightedPillColor={PRESCRIPTION_COLORS_MAP.observations.highlightColor}
            title="Symptoms and findings"
            showContextFormOnClickNewCondition={(item) => {
              return item?.severity || item?.note || item?.duration
            }}
            useFullPageSearch={true}
          />
          <Dialog
            open={Boolean(addObservationButtonAnchor)}
            onClose={() => {
              setAddObservationButtonAnchor(null)
            }}
          >
            <HealthEntityAddPopover
              healthEntity={observationToCreate}
              addToHealthEntityArray={createObservations}
              preferredLanguages={prescriptionViewSettings?.preferredPrescriptionLanguages}
              closePopover={() => setAddObservationButtonAnchor(null)}
              healthEntityName="Observations"
              apiParam="health-observation"
            />
          </Dialog>
        </>
      )
    case PRESCRIPTION_BUILDER_VIEW_TYPES.modern:
      return (
        <>
          <PrescriptionTableViewInputComponent
            value={observations}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            getOptionsResponse={loadOptionsObservations}
            searchPlaceholder="Observation"
            onChange={onChangeForNewUIItems}
            onCreate={onCreateObservations}
            prescriptionViewSettings={prescriptionViewSettings}
            debounceTimeout={200}
            additional={{
              page: 1,
              clinicId: clinicId,
              clinicBrandId: clinicBrandId,
              recommendedObservations,
              prescriptionViewSettings,
            }}
          />
          <Popover
            anchorEl={addObservationButtonAnchor}
            open={Boolean(addObservationButtonAnchor)}
            onClose={() => {
              setAddObservationButtonAnchor(null)
            }}
          >
            <HealthEntityAddPopover
              healthEntity={observationToCreate}
              addToHealthEntityArray={createObservations}
              preferredLanguages={prescriptionViewSettings?.preferredPrescriptionLanguages}
              closePopover={() => setAddObservationButtonAnchor(null)}
              healthEntityName="Observations"
              apiParam="health-observation"
            />
          </Popover>
        </>
      )
    default:
      return (
        <ObservationsSelectComponent
          clinicId={clinicId}
          clinicBrandId={clinicBrandId}
          observations={observations}
          setSelectedObservations={setSelectedObservations}
          recommendedObservations={recommendedObservations}
          prescriptionViewSettings={prescriptionViewSettings}
        />
      )
  }
}

{
  /* <ObservationsInputComponent
          clinicId={clinicId}
          clinicBrandId={clinicBrandId}
          observations={observations}
          setSelectedObservations={setSelectedObservations}
          recommendedObservations={recommendedObservations}
        /> */
}

export const ObservationsSelectComponent = ({
  clinicId,
  clinicBrandId,
  observations,
  setSelectedObservations,
  recommendedObservations = [],
  prescriptionViewSettings,
}) => {
  const [inputValue, setInputValue] = useState('')

  const handleSelectChange = (selectedValues) => {
    setSelectedObservations(
      selectedValues?.map((item) => {
        const label = item?.label || item?.value
        return { ...item, label: getFirstLetterCapitalizedString(label) }
      }),
    )
  }

  return (
    <div style={{ width: '100%' }}>
      <ObservationsCreatableSelect
        id="observations-select"
        classNamePrefix="observations_selection"
        closeMenuOnSelect={false}
        placeholder="Observation"
        isMulti={true}
        isClearable={true}
        isSearchable={true}
        defaultOptions={false}
        loadOptions={loadOptionsObservations}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        value={observations}
        onChange={handleSelectChange}
        debounceTimeout={200}
        additional={{
          page: 1,
          clinicId: clinicId,
          clinicBrandId: clinicBrandId,
          prescriptionViewSettings,
          recommendedObservations,
        }}
        styles={commonStylesPrescriptionSelect}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        inputValue={inputValue}
        onInputChange={(a, b) => {
          setInputValue((prev) => {
            if (prev === a) {
              return prev
            }
            return a || ''
          })
          handleChangeHealthEntityInputType1(a, b, setSelectedObservations)
        }}
        onKeyDown={(e) =>
          handleKeyDownAndSupportEdit(
            e,
            inputValue,
            observations,
            setSelectedObservations,
            setInputValue,
          )
        }
      />
    </div>
  )
}
